"use client"
import { motion, AnimatePresence } from "framer-motion"
import { X, CheckCircle2, XCircle } from "lucide-react"
import { Button } from "./button"

interface PopupProps {
    isOpen: boolean
    onClose: () => void
    type: "success" | "error"
    title: string
    message: string
    primaryButtonLabel?: string
    secondaryButtonLabel?: string
    onPrimaryClick?: () => void
    onSecondaryClick?: () => void
}

export const Popup = ({
    isOpen,
    onClose,
    type,
    title,
    message,
    primaryButtonLabel = "OK",
    secondaryButtonLabel = "Cancel",
    onPrimaryClick,
    onSecondaryClick,
}: PopupProps) => {
    const handlePrimaryClick = () => {
        if (onPrimaryClick) {
            onPrimaryClick()
        }
        onClose()
    }

    const handleSecondaryClick = () => {
        if (onSecondaryClick) {
            onSecondaryClick()
        }
        onClose()
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Popup */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 30
                            }}
                            className="relative w-full max-w-md rounded-2xl border bg-background p-6 shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                aria-label="Close popup"
                            >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </button>

                            {/* Content */}
                            <div className="flex flex-col items-center text-center space-y-4">
                                {/* Icon */}
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                                    className={`rounded-full p-3 ${type === "success"
                                        ? "bg-green-500/10"
                                        : "bg-red-500/10"
                                        }`}
                                >
                                    {type === "success" ? (
                                        <CheckCircle2 className="h-8 w-8 text-green-500" />
                                    ) : (
                                        <XCircle className="h-8 w-8 text-red-500" />
                                    )}
                                </motion.div>

                                {/* Title */}
                                <motion.h3
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="text-xl font-semibold text-foreground"
                                >
                                    {title}
                                </motion.h3>

                                {/* Message */}
                                <motion.p
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="text-sm text-muted-foreground"
                                >
                                    {message}
                                </motion.p>

                                {/* Buttons */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="flex w-full gap-3 pt-2"
                                >
                                    <Button
                                        variant="outline"
                                        onClick={handleSecondaryClick}
                                        className="flex-1"
                                    >
                                        {secondaryButtonLabel}
                                    </Button>
                                    <Button
                                        variant={type === "success" ? "default" : "destructive"}
                                        onClick={handlePrimaryClick}
                                        className="flex-1"
                                    >
                                        {primaryButtonLabel}
                                    </Button>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    )
}

